import { useContext, useEffect, useState } from 'react';
import { Table, Checkbox, Button, Tooltip, Pagination } from 'antd';
import { Link } from 'react-router-dom';
import { EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined, DownloadOutlined, ArrowRightOutlined } from '@ant-design/icons';
import useModal from '../../../Hooks/useModal';
import ModalDelete from '../ModalDelete';
import useCan from '../../../Hooks/useCan';
import { ExternalRoles } from '../../../Static/Roles';
import ModalDeletePathComponent from '../../PathManager/ModalDeletePathComponent';
import { PathsInFileManager } from '../../../Static/PathsInFileManager';
import { formatDate } from '../../../Utils/DateFormat';
import { CompanyContext } from "../../../Contexts/CompanyContext";
interface ITableDefaultProps {
    columns: any[],
    rows: any[],
    firstColumnIsLink?: boolean,
    linkToFirstColumn?: string,
    lastColumnIsAction?: boolean,
    total?: number,
    changePage?: (page: number) => Promise<void>,
    linkToEdit?: string,
    functionToDelete?: (id: string) => Promise<void>,
    ColumnWithButton?: number,
    columnActionToButton?: (object: any) => void,
    hasHeader: boolean,
    hasDelete?: boolean,
    hasEdit?: boolean,
    hasGoTo?: boolean,
    hasSeeMore?: boolean,
    linkToSeeMore?: string,
    linkGoTo?: string,
    isNews?: boolean,
    isFiles?: boolean,
    hasPagination: boolean,
    firstColumnIsCheck?: boolean,
    editIsButton?: boolean,
    functionToEdit?: (object: any) => void,
    setCheckedState?: React.Dispatch<React.SetStateAction<any[]>>,
    checkedState?: any[],
    pathMain?: string,
    hasTitleOnLines?: boolean,
    hasModalInfo?: boolean,
    toggleShowModalInfo?: React.Dispatch<React.SetStateAction<void>>,
    setModalInfo?: any,
    isNotMonetary?: number[],
    hasDownload?: boolean,
    functionToDownload?: (object: any) => void,
    canDelete?: boolean,
    isProjectInformation?: boolean
}
const TableDefault = (props: ITableDefaultProps) => {
    const canNotDelete = useCan({ rolesCan: [...ExternalRoles] });
    const canNotView = useCan({ rolesCan: ["Internal", "TechnicianLimited", "Technician", "Dossie", "Financial"] });
    const { showModal, toggleShowModal } = useModal();
    const { showModal: showModalPath, toggleShowModal: toggleShowModalPath } = useModal();
    const [deleteResponse, setDeleteResponse] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<string>("");
    const [checkAll, setCheckAll] = useState<boolean>(false);
    const { company } = useContext(CompanyContext);
    const handleCheckAll = (checked: boolean) => {
        setCheckAll(checked);
        if (props.setCheckedState) {
            const updatedState = props.rows.map(() => checked);
            props.setCheckedState(updatedState);
        }
    };
    const handleCheckRow = (index: number) => {
        if (props.checkedState && props.setCheckedState) {
            const updatedState = props.checkedState.map((item, idx) => (idx === index ? !item : item));
            props.setCheckedState(updatedState);
            setCheckAll(updatedState.every(item => item));
        }
    };
    useEffect(() => {
        if (deleteResponse && itemToDelete !== "") {
            props.functionToDelete?.(itemToDelete);
            setDeleteResponse(false);
            setItemToDelete("");
        }
    }, [deleteResponse, itemToDelete, props]);
    const renderCell = (value: any, columnIndex: number, row: any) => {
        if (props.isNotMonetary?.includes(columnIndex)) return value;
        if (typeof value === "number" && !isNaN(value)) {
            return canNotView ? "R$ --" : `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
        }
        return value;
    };
    const tableColumns = [
        props.firstColumnIsCheck && {
            title: <Checkbox checked={checkAll} onChange={(e) => handleCheckAll(e.target.checked)} />,
            render: (_: any, __: any, index: number) => (
                <Checkbox
                    checked={props.checkedState ? props.checkedState[index] : false}
                    onChange={() => handleCheckRow(index)}
                />
            ),
            width: 50,
        },
        ...props.columns.map((column, i) => ({
            title: column.name,
            dataIndex: column.id,
            render: (value: any, row: any) => renderCell(value, i, row),
        })),
        props.lastColumnIsAction && {
            title: "Ação",
            render: (row: any) => (
                <div style={{ display: "flex", justifyContent: "space-around" }}>
                    {props.hasModalInfo && (
                        <Tooltip title="Visualizar">
                            <Button
                                icon={<EyeOutlined />}
                                onClick={() => {
                                    props.setModalInfo?.(row);
                                    props.toggleShowModalInfo?.();
                                }}
                            />
                        </Tooltip>
                    )}
                    {props.hasSeeMore && (
                        <Link to={`${props.linkToSeeMore}${row.id}`}>
                            <Tooltip title="Detalhes">
                                <Button icon={<SearchOutlined />} />
                            </Tooltip>
                        </Link>
                    )}
                    {props.hasEdit && (
                        <Link to={`${props.linkToEdit}${row.id}`}>
                            <Tooltip title="Editar">
                                <Button icon={<EditOutlined />} />
                            </Tooltip>
                        </Link>
                    )}
                    {props.hasDelete && (
                        <Tooltip title="Deletar">
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => {
                                    setItemToDelete(row.id);
                                    props.pathMain ? toggleShowModalPath() : toggleShowModal();
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ].filter(Boolean);
    return (
        <div>
            <ModalDelete show={showModal} toggleShow={toggleShowModal} setResponse={setDeleteResponse} />
            <ModalDeletePathComponent
                pathName={PathsInFileManager.find((x) => x.id === props.pathMain)?.name ?? ""}
                show={showModalPath}
                toggleShow={toggleShowModalPath}
                setResponse={setDeleteResponse}
            />
            <Table
                columns={tableColumns as any}
                dataSource={props.rows}
                rowKey="id"
                pagination={false}
                bordered
            />
            {props.hasPagination && props.total && (
                <Pagination
                    total={props.total}
                    pageSize={10}
                    onChange={(page) => props.changePage?.(page)}
                    style={{ marginTop: 16, textAlign: "right" }}
                />
            )}
        </div>
    );
};
export default TableDefault;